import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import axios from 'axios';
import { Star, Upload, X, Crown, AlertCircle, CheckCircle, Image } from 'lucide-react';
import './ReviewForm.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function ReviewForm({ itemType, itemId, onSuccess }) {
  const { user, token, isPremium } = useAuth();
  
  const [formData, setFormData] = useState({
    rating: 5,
    title: '',
    comment: ''
  });
  
  const [photos, setPhotos] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);

  const handleRatingChange = (newRating) => {
    setFormData(prev => ({ ...prev, rating: newRating }));
  };

  const handleFileSelect = async (e) => {
    if (!isPremium) {
      setError('Foto-Upload ist nur für Premium-Mitglieder verfügbar');
      return;
    }

    const files = Array.from(e.target.files);
    
    if (files.length + photos.length > 5) {
      setError('Maximal 5 Fotos erlaubt');
      return;
    }

    setUploading(true);
    setError('');

    for (const file of files) {
      try {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(`${API}/upload-photo`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
            Authorization: `Bearer ${token}`
          }
        });

        setPhotos(prev => [...prev, response.data.url]);
      } catch (err) {
        setError(err.response?.data?.detail || 'Fehler beim Hochladen');
      }
    }

    setUploading(false);
  };

  const removePhoto = (photoUrl) => {
    setPhotos(prev => prev.filter(p => p !== photoUrl));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSubmitting(true);

    try {
      await axios.post(`${API}/reviews`, {
        user_id: user.id,
        user_name: user.name,
        item_type: itemType,
        item_id: itemId,
        rating: formData.rating,
        title: formData.title,
        comment: formData.comment,
        photos: photos
      }, {
        headers: {
          Authorization: `Bearer ${token}`
        }
      });

      setSuccess(true);
      setTimeout(() => {
        setFormData({ rating: 5, title: '', comment: '' });
        setPhotos([]);
        setSuccess(false);
        if (onSuccess) onSuccess();
      }, 2000);
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Absenden der Bewertung');
    } finally {
      setSubmitting(false);
    }
  };

  if (!user) {
    return (
      <div className="review-form-login-prompt">
        <AlertCircle size={24} />
        <p>Bitte melde dich an, um eine Bewertung abzugeben.</p>
      </div>
    );
  }

  if (success) {
    return (
      <div className="review-success">
        <CheckCircle size={48} />
        <h3>Bewertung erfolgreich abgegeben!</h3>
        <p>Vielen Dank für dein Feedback.</p>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="review-form">
      <h3>Bewertung abgeben</h3>

      {error && (
        <div className="review-error">
          <AlertCircle size={20} />
          <span>{error}</span>
        </div>
      )}

      {/* Rating Stars */}
      <div className="rating-input">
        <label>Bewertung *</label>
        <div className="stars">
          {[1, 2, 3, 4, 5].map(rating => (
            <button
              key={rating}
              type="button"
              onClick={() => handleRatingChange(rating)}
              className={`star-button ${formData.rating >= rating ? 'active' : ''}`}
            >
              <Star size={32} fill={formData.rating >= rating ? '#fbbf24' : 'none'} />
            </button>
          ))}
        </div>
      </div>

      {/* Title */}
      <div className="form-group">
        <label htmlFor="title">Titel *</label>
        <input
          id="title"
          type="text"
          value={formData.title}
          onChange={(e) => setFormData(prev => ({ ...prev, title: e.target.value }))}
          placeholder="Zusammenfassung deiner Erfahrung"
          required
        />
      </div>

      {/* Comment */}
      <div className="form-group">
        <label htmlFor="comment">Kommentar *</label>
        <textarea
          id="comment"
          value={formData.comment}
          onChange={(e) => setFormData(prev => ({ ...prev, comment: e.target.value }))}
          placeholder="Teile deine Erfahrungen..."
          rows={5}
          required
        />
      </div>

      {/* Photo Upload */}
      <div className="photo-upload-section">
        <div className="photo-upload-header">
          <label>
            <Image size={20} />
            Fotos hinzufügen
            {!isPremium && (
              <span className="premium-badge-small">
                <Crown size={14} />
                Premium
              </span>
            )}
          </label>
          {isPremium && (
            <span className="photo-count">{photos.length}/5</span>
          )}
        </div>

        {isPremium ? (
          <>
            <input
              type="file"
              id="photo-input"
              accept="image/jpeg,image/jpg,image/png,image/webp"
              multiple
              onChange={handleFileSelect}
              disabled={uploading || photos.length >= 5}
              style={{ display: 'none' }}
            />
            <label htmlFor="photo-input" className={`photo-upload-button ${uploading || photos.length >= 5 ? 'disabled' : ''}`}>
              <Upload size={20} />
              {uploading ? 'Wird hochgeladen...' : 'Fotos auswählen'}
            </label>

            {photos.length > 0 && (
              <div className="photos-preview">
                {photos.map((photo, index) => (
                  <div key={index} className="photo-preview">
                    <img src={`${BACKEND_URL}${photo}`} alt={`Upload ${index + 1}`} />
                    <button
                      type="button"
                      onClick={() => removePhoto(photo)}
                      className="remove-photo"
                    >
                      <X size={16} />
                    </button>
                  </div>
                ))}
              </div>
            )}
          </>
        ) : (
          <div className="premium-photo-teaser">
            <Crown size={24} />
            <p>Upgrade auf Premium um Fotos hochzuladen</p>
          </div>
        )}
      </div>

      <button type="submit" className="submit-review-button" disabled={submitting}>
        {submitting ? 'Wird gesendet...' : 'Bewertung abschicken'}
      </button>
    </form>
  );
}
